import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import CopyFill from '@/assets/icons/copy_fill.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useCopyElementAsImage } from '@/hooks/useCopyElementAsImage';
import { COPY_EXCLUDE_ATTR } from '@/utils/elementCapture';

import styles from './CopyAsImageButton.module.scss';

interface CopyAsImageButtonProps {
    /** The subtree to rasterise. Nodes marked with `data-copy-exclude` are left out. */
    targetRef: RefObject<HTMLElement | null>;
    /** Reported to analytics as the `exportType` of the copy. */
    exportType: string;
    /**
     * Background painted behind the capture, as any valid CSS color. Defaults to the surface
     * colour of the theme on screen.
     */
    backgroundColor?: string;
    disabled?: boolean;
}

export default function CopyAsImageButton(props: CopyAsImageButtonProps) {
    const { targetRef, exportType, backgroundColor, disabled = false } = props;
    const { t } = useTranslation();

    const { copyAsImage, isCopying, isSupported } = useCopyElementAsImage({
        exportType,
        backgroundColor,
    });

    if (!isSupported) {
        return null;
    }

    return (
        // The button sits inside the captured subtree, so it has to opt itself out.
        <div className={styles.copyAsImageButton} {...{ [COPY_EXCLUDE_ATTR]: '' }}>
            <Button
                variant="secondary"
                disabled={disabled || isCopying}
                trackingId="copy_as_image"
                onClick={() => copyAsImage(targetRef.current)}
                icon={
                    <SvgIcon color="currentColor" icon={CopyFill} size="custom" customSize={20} />
                }>
                {isCopying ? t('molecules.copyAsImage.copying') : t('molecules.copyAsImage.button')}
            </Button>
        </div>
    );
}
